import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, GitMerge, Search, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { formatCurrency } from "./inventoryConstants";

export const TransferStockDialog = ({ open, onClose, part, locations, currentUser, onDone }) => {
  const [fromId, setFromId] = useState("");
  const [toId, setToId] = useState("");
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";
  const buckets = part?.stock_locations || [];
  const unallocated = part?.unallocated ?? 0;

  useEffect(() => { if (open) { setFromId(""); setToId(""); setQty(1); } }, [open]);
  if (!part) return null;

  const submit = async () => {
    if (!toId) { toast.error("Choose a destination location"); return; }
    if (fromId && fromId === toId) { toast.error("Source and destination must differ"); return; }
    setSaving(true);
    try {
      await inventoryApi.transferPartLocation(part.id, {
        from_location_id: fromId || null, to_location_id: toId, quantity: Number(qty) || 0, user: userName,
      });
      toast.success("Stock moved");
      onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Transfer failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="transfer-stock-dialog">
        <DialogTitle className="sr-only">Move Stock Between Locations</DialogTitle>
        <DialogDescription className="sr-only">Move stock between storage locations</DialogDescription>
        <h3 className="text-lg font-semibold mb-1">Move / Allocate Stock</h3>
        <p className="text-xs text-slate-500 mb-3">Total on hand stays the same — this just changes where it's stored.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">From</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={fromId} onChange={(e) => setFromId(e.target.value)} data-testid="transfer-from">
              <option value="">Unassigned ({unallocated})</option>
              {buckets.map((b) => <option key={b.location_id || "none"} value={b.location_id || ""}>{b.location_label || "Unknown"} ({b.quantity})</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">To</Label>
            <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={toId} onChange={(e) => setToId(e.target.value)} data-testid="transfer-to">
              <option value="">Select location...</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.label || l.container}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Quantity</Label>
            <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="transfer-qty" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button className="flex-1" onClick={submit} disabled={saving} data-testid="transfer-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4 mr-1" /> Move</>}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MergePartDialog = ({ open, onClose, part, currentUser, onDone }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [source, setSource] = useState(null);
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  useEffect(() => { if (open) { setQuery(""); setResults([]); setSource(null); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (!query.trim()) { setResults([]); return; }
      try {
        const parts = await inventoryApi.getParts({ search: query });
        setResults(parts.filter((p) => p.id !== part?.id).slice(0, 8));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, part?.id]);

  if (!part) return null;

  const submit = async () => {
    if (!source) { toast.error("Select a duplicate part to merge in"); return; }
    setSaving(true);
    try {
      await inventoryApi.mergeParts(part.id, { source_id: source.id, user: userName });
      toast.success(`Merged ${source.part_number} into ${part.part_number}`);
      onDone(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Merge failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="merge-part-dialog">
        <DialogTitle className="sr-only">Merge Duplicate Part</DialogTitle>
        <DialogDescription className="sr-only">Merge a duplicate part into this one</DialogDescription>
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2"><GitMerge className="w-5 h-5 text-blue-600" /> Merge Duplicate Into This Part</h3>
        <p className="text-xs text-slate-500 mb-3">Keeping <span className="font-medium">{part.part_number}</span>. The selected duplicate's stock moves here (weighted-average cost) and its part number is kept as an alternate.</p>

        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search the duplicate part..." value={query} onChange={(e) => setQuery(e.target.value)} data-testid="merge-search" />
        </div>

        <div className="max-h-48 overflow-y-auto space-y-1 mb-3">
          {results.map((p) => (
            <button key={p.id} onClick={() => setSource(p)} data-testid={`merge-option-${p.id}`}
              className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${source?.id === p.id ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
              <div className="font-medium text-slate-800">{p.name} <span className="font-mono text-xs text-slate-400">{p.part_number}</span></div>
              <div className="text-xs text-slate-500">On hand {p.quantity_on_hand} · {formatCurrency(p.inventory_value)}</div>
            </button>
          ))}
          {query && results.length === 0 && <div className="text-sm text-slate-400 px-1 py-2">No matches</div>}
        </div>

        {source && (
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mb-3">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <span><b>{source.part_number}</b> will be voided after merging. Its {source.quantity_on_hand} units and history are preserved on the audit trail.</span>
          </div>
        )}

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" onClick={submit} disabled={!source || saving} data-testid="merge-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Merge"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
